import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SearchState, Search, SearchLimitError, CreateSearchParams, SearchListParams, PaginationResponse } from '../../types/search.types';
import * as searchApi from '../../api/search';

interface SearchSliceState extends SearchState {
  pagination: {
    page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    has_next: boolean;
    has_previous: boolean;
  } | null;
  statistics: {
    total_completed: number;
    total_processing: number;
    total_failed: number;
  } | null;
}

const initialState: SearchSliceState = {
  searches: [],
  currentSearch: null,
  loading: false,
  error: null,
  pagination: null,
  statistics: null,
};

const formatError = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    return typeof error.response.data.detail === 'string'
      ? error.response.data.detail
      : JSON.stringify(error.response.data.detail);
  }
  return 'An unexpected error occurred';
};

const isSearchLimitDetail = (detail: unknown): detail is SearchLimitError =>
  typeof detail === 'object' &&
  detail !== null &&
  'message' in detail &&
  typeof (detail as SearchLimitError).message === 'string' &&
  'active_searches' in detail &&
  typeof (detail as SearchLimitError).active_searches === 'number' &&
  'max_allowed' in detail &&
  typeof (detail as SearchLimitError).max_allowed === 'number' &&
  'current_plan' in detail &&
  typeof (detail as SearchLimitError).current_plan === 'string';

export const createSearch = createAsyncThunk(
  'search/createSearch',
  async (params: CreateSearchParams, { rejectWithValue }) => {
    try {
      return await searchApi.createSearch(params);
    } catch (error: any) {
      if (error?.response?.status === 402) {
        return rejectWithValue(error.response.data);
      }
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 400 && isSearchLimitDetail(detail)) {
        return rejectWithValue(detail);
      }
      return rejectWithValue(formatError(error));
    }
  }
);

export const fetchSearches = createAsyncThunk(
  'search/fetchSearches',
  async (params: SearchListParams | undefined, { rejectWithValue }) => {
    try {
      return await searchApi.getSearches(params);
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

export const fetchSearch = createAsyncThunk(
  'search/fetchSearch',
  async (id: number, { rejectWithValue }) => {
    try {
      return await searchApi.getSearch(id);
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

export const deleteSearch = createAsyncThunk(
  'search/deleteSearch',
  async (id: number, { rejectWithValue }) => {
    try {
      await searchApi.deleteSearch(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSearch: (state) => {
      state.currentSearch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create search
      .addCase(createSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.searches.push(action.payload);
        state.currentSearch = action.payload;
      })
      .addCase(createSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | SearchLimitError;
      })
      
      // Fetch searches
      .addCase(fetchSearches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearches.fulfilled, (state, action) => {
        state.loading = false;
        state.searches = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          total_pages: action.payload.total_pages,
          total_items: action.payload.total_items,
          items_per_page: action.payload.items_per_page,
          has_next: action.payload.has_next,
          has_previous: action.payload.has_previous,
        };
        state.statistics = {
          total_completed: action.payload.total_completed,
          total_processing: action.payload.total_processing,
          total_failed: action.payload.total_failed,
        };
      })
      .addCase(fetchSearches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.pagination = null;
        state.statistics = null;
      })
      
      // Fetch single search
      .addCase(fetchSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSearch = action.payload;
        
        // Update in the searches array if exists
        const index = state.searches.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.searches[index] = action.payload;
        }
      })
      .addCase(fetchSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete search
      .addCase(deleteSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.searches = state.searches.filter(s => s.id !== action.payload);
        
        // Clear current search if it was the one deleted
        if (state.currentSearch && state.currentSearch.id === action.payload) {
          state.currentSearch = null;
        }
      })
      .addCase(deleteSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentSearch } = searchSlice.actions;
export default searchSlice.reducer; 